import React, { useState, useEffect } from 'react';
import { 
    Box, 
    Typography, 
    Grid, 
    Paper,
    Divider
} from '@mui/material';
import { SandboxConsentDetails } from '../../../services/sandbox-consents-service';
import { languageService, Language } from '../../../services/language-service';

interface ContactDpoTabProps {
    consentData: SandboxConsentDetails;
}

const ContactDpoTab: React.FC<ContactDpoTabProps> = ({ consentData }) => {
    const { contactDpo } = consentData;
    const [languages, setLanguages] = useState<Language[]>([]);

    // Load languages on component mount
    useEffect(() => {
        const loadLanguages = async () => {
            try {
                const languageData = await languageService.getSupportedLanguages();
                setLanguages(languageData);
            } catch (error) {
                console.error('Failed to load languages:', error);
            }
        };
        loadLanguages();
    }, []);

    // Get language name from code
    const getLanguageName = (languageCode: string): string => {
        // Hardcode English for 'en' as requested
        if (languageCode.toLowerCase() === 'en') {
            return 'English';
        }
        
        const language = languages.find(lang => 
            lang.code.toLowerCase() === languageCode.toLowerCase()
        );
        
        if (language) {
            if (language.name !== language.localeName) {
                return `${language.name} (${language.localeName})`;
            }
            return language.name;
        }
        
        // Fallback to uppercase code if not found
        return languageCode.toUpperCase();
    };

    // Check if contact DPO data exists
    if (!contactDpo || !contactDpo.json) {
        return (
            <Box>
                <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
                    Contact DPO Details
                </Typography>
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                    <Typography variant="body1" color="text.secondary">
                        No contact DPO information available for this consent.
                    </Typography>
                </Paper>
            </Box>
        );
    }

    const dpoJson = contactDpo.json;

    return (
        <Box>
            <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
                Contact DPO Details
            </Typography>

            <Grid container spacing={3}>
                {/* Left Column - Metadata (30% width) */}
                <Grid item xs={12} md={3.6}>
                    <Paper sx={{ p: 3, height: 'fit-content' }}>
                        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'medium', color: 'primary.main' }}>
                            DPO Information
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {/* Title */}
                            {dpoJson.title && (
                                <Box>
                                    <Typography variant="caption" color="text.secondary">Title</Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 'medium', color: 'text.primary' }}>
                                        {dpoJson.title}
                                    </Typography>
                                </Box>
                            )}

                            {/* Language */}
                            {contactDpo.languageCode && (
                                <Box>
                                    <Typography variant="caption" color="text.secondary">Language</Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 'medium', color: 'text.primary' }}>
                                        {getLanguageName(contactDpo.languageCode)}
                                    </Typography>
                                </Box>
                            )}

                            {/* Version from JSON */}
                            {dpoJson.version && (
                                <Box>
                                    <Typography variant="caption" color="text.secondary">Version (JSON)</Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 'medium', color: 'text.primary' }}>
                                        {dpoJson.version}
                                    </Typography>
                                </Box>
                            )}

                            {/* Version from metadata */}
                            {contactDpo.version && contactDpo.version !== dpoJson.version && (
                                <Box>
                                    <Typography variant="caption" color="text.secondary">Version (Metadata)</Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 'medium', color: 'text.primary' }}>
                                        {contactDpo.version}
                                    </Typography>
                                </Box>
                            )}

                            {/* Effective Date from JSON */}
                            {dpoJson.effectiveDate && (
                                <Box>
                                    <Typography variant="caption" color="text.secondary">Effective Date (JSON)</Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 'medium', color: 'text.primary' }}>
                                        {dpoJson.effectiveDate}
                                    </Typography>
                                </Box>
                            )}

                            {/* Effective Date from metadata */}
                            {contactDpo.effectiveDate && contactDpo.effectiveDate !== dpoJson.effectiveDate && (
                                <Box>
                                    <Typography variant="caption" color="text.secondary">Effective Date (Metadata)</Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 'medium', color: 'text.primary' }}>
                                        {contactDpo.effectiveDate}
                                    </Typography>
                                </Box>
                            )}

                            {/* Last Updated from JSON */}
                            {dpoJson.lastUpdated && (
                                <Box>
                                    <Typography variant="caption" color="text.secondary">Last Updated (JSON)</Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 'medium', color: 'text.primary' }}>
                                        {dpoJson.lastUpdated}
                                    </Typography>
                                </Box>
                            )}

                            {/* Last Updated from metadata */}
                            {contactDpo.lastUpdated && contactDpo.lastUpdated !== dpoJson.lastUpdated && (
                                <Box>
                                    <Typography variant="caption" color="text.secondary">Last Updated (Metadata)</Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 'medium', color: 'text.primary' }}>
                                        {contactDpo.lastUpdated}
                                    </Typography>
                                </Box>
                            )}

                            {/* Compliance Text */}
                            {dpoJson.complianceText && (
                                <Box>
                                    <Typography variant="caption" color="text.secondary">Compliance</Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 'medium', color: 'text.primary' }}>
                                        {dpoJson.complianceText}
                                    </Typography>
                                </Box>
                            )}

                            {/* Document Info */}
                            {dpoJson.documentInfo && (
                                <Box>
                                    <Typography variant="caption" color="text.secondary">Document Info</Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 'medium', color: 'text.primary' }}>
                                        {dpoJson.documentInfo}
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    </Paper>
                </Grid>

                {/* Right Column - Content (70% width) */}
                <Grid item xs={12} md={8.4}>
                    <Paper sx={{ p: 3 }}>
                        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'medium', color: 'primary.main' }}>
                            Contact DPO Content
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        
                        {dpoJson.content ? (
                            <Box 
                                sx={{ 
                                    maxHeight: '70vh',
                                    overflowY: 'auto',
                                    pr: 1,
                                    color: 'text.primary',
                                    '&::-webkit-scrollbar': {
                                        width: '8px',
                                    },
                                    '&::-webkit-scrollbar-track': {
                                        backgroundColor: 'grey.100',
                                        borderRadius: '4px',
                                    },
                                    '&::-webkit-scrollbar-thumb': {
                                        backgroundColor: 'grey.400',
                                        borderRadius: '4px',
                                        '&:hover': {
                                            backgroundColor: 'grey.500',
                                        },
                                    },
                                    '& h1, & h2, & h3, & h4, & h5, & h6': {
                                        color: 'text.primary',
                                        marginTop: 2,
                                        marginBottom: 1,
                                        fontWeight: 600
                                    },
                                    '& p': {
                                        marginBottom: 1.5,
                                        lineHeight: 1.6,
                                        color: 'text.primary'
                                    },
                                    // Override for paragraphs NOT in highlight sections
                                    '& p:not(.highlight p)': {
                                        color: 'text.primary'
                                    },
                                    '& ul, & ol': {
                                        marginBottom: 1.5,
                                        paddingLeft: 2,
                                        color: 'text.primary'
                                    },
                                    '& li': {
                                        marginBottom: 0.75,
                                        color: 'text.primary',
                                        lineHeight: 1.5
                                    },
                                    '& strong': {
                                        color: 'text.primary',
                                        fontWeight: 600
                                    },
                                    '& a': {
                                        color: 'primary.main',
                                        textDecoration: 'none',
                                        '&:hover': {
                                            textDecoration: 'underline'
                                        }
                                    },
                                    '& div, & span': {
                                        color: 'text.primary !important'
                                    },
                                    '& .faq, & .question, & .answer': {
                                        color: 'text.primary !important'
                                    },
                                    '& *': {
                                        color: 'text.primary !important'
                                    },
                                    '& [style*="color"]': {
                                        color: 'text.primary !important'
                                    },
                                    '& [class*="faq"]': {
                                        color: 'text.primary !important'
                                    },
                                    '& [class*="question"]': {
                                        color: 'text.primary !important'
                                    },
                                    '& [class*="answer"]': {
                                        color: 'text.primary !important'
                                    },
                                    '& p[style*="color"]': {
                                        color: 'text.primary !important'
                                    },
                                    '& div[style*="color"]': {
                                        color: 'text.primary !important'
                                    },
                                    '& span[style*="color"]': {
                                        color: 'text.primary !important'
                                    },
                                    '& [style*="background"]': {
                                        backgroundColor: 'background.paper !important',
                                        color: 'text.primary !important'
                                    },
                                    '& [class*="faq"], & [class*="question"], & [class*="answer"]': {
                                        backgroundColor: 'background.paper !important',
                                        color: 'text.primary !important'
                                    },
                                    '& div[style*="background-color"]': {
                                        backgroundColor: 'background.paper !important',
                                        color: 'text.primary !important'
                                    },
                                    '& p[style*="background-color"]': {
                                        backgroundColor: 'background.paper !important',
                                        color: 'text.primary !important'
                                    },
                                    '& span[style*="background-color"]': {
                                        backgroundColor: 'background.paper !important',
                                        color: 'text.primary !important'
                                    },
                                    '& *[style*="color: white"], & *[style*="color: #fff"], & *[style*="color: #ffffff"]': {
                                        color: 'text.primary !important'
                                    },
                                    '& *[style*="color: lightgray"], & *[style*="color: #lightgray"], & *[style*="color: #d3d3d3"]': {
                                        color: 'text.primary !important'
                                    },
                                    '& *[style*="color: lightgrey"], & *[style*="color: #lightgrey"]': {
                                        color: 'text.primary !important'
                                    },
                                    '& *[style*="color: #f0f0f0"], & *[style*="color: #e0e0e0"], & *[style*="color: #d0d0d0"]': {
                                        color: 'text.primary !important'
                                    },
                                    '& *[style*="color: #c0c0c0"], & *[style*="color: #b0b0b0"], & *[style*="color: #a0a0a0"]': {
                                        color: 'text.primary !important'
                                    },
                                    '& .contact-info': {
                                        backgroundColor: 'background.paper',
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        padding: 2,
                                        borderRadius: 1,
                                        marginBottom: 2,
                                        '& h3': {
                                            color: 'primary.main',
                                            marginBottom: 1
                                        },
                                        '& p': {
                                            color: 'text.primary',
                                            marginBottom: 0.5
                                        },
                                        '& *': {
                                            color: 'text.primary !important'
                                        }
                                    },
                                    '& .highlight': {
                                        backgroundColor: 'primary.main',
                                        color: 'primary.contrastText',
                                        padding: 3,
                                        borderRadius: 2,
                                        marginBottom: 2,
                                        border: '1px solid',
                                        borderColor: 'primary.dark',
                                        '& h3': {
                                            color: 'primary.contrastText',
                                            marginBottom: 1.5,
                                            fontWeight: 600
                                        },
                                        '& ul': {
                                            color: 'primary.contrastText',
                                            marginBottom: 0
                                        },
                                        '& li': {
                                            color: 'primary.contrastText',
                                            marginBottom: 0.75,
                                            lineHeight: 1.5
                                        },
                                        '& strong': {
                                            color: 'primary.contrastText',
                                            fontWeight: 600
                                        },
                                        '& p, & p *': {
                                            color: 'primary.contrastText !important'
                                        },
                                        '& *': {
                                            color: 'primary.contrastText !important'
                                        }
                                    }
                                }}
                                dangerouslySetInnerHTML={{ __html: dpoJson.content }}
                            />
                        ) : (
                            <Typography variant="body1" color="text.secondary">
                                No content available for contact DPO information.
                            </Typography>
                        )}
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
};

export default ContactDpoTab;
